/**
 * Next.js 15+ Client Instrumentation hook — runs once when the client starts.
 *
 * This is the official replacement for the top-level `sentry.client.config.ts`
 * pattern. Next.js automatically imports this file on the client side before
 * any component renders, ensuring Sentry is ready for error capture from the
 * first paint.
 *
 * Performance monitoring (transactions) is disabled — distributed tracing
 * is handled by OpenTelemetry instead.
 *
 * @see https://nextjs.org/docs/app/building-your-application/optimizing/instrumentation#client-instrumentation
 */

// Must come before anything that may load pdfjs-dist — pdfjs calls
// Map.prototype.getOrInsertComputed, which Safari <26 does not ship.
import '@/lib/polyfills/map-upsert';

import * as Sentry from '@sentry/nextjs';

// Sentry error tracking — legitimate interest (GDPR Art. 6(1)(f)).
// Pure error monitoring with no analytics cross-linking: no PostHog
// distinct_id, no session replay URLs, no tracking cookies set.
// Only captures unhandled errors + navigation breadcrumbs.
Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT || process.env.NODE_ENV,

  // Performance monitoring disabled — tracing handled by OpenTelemetry.
  tracesSampleRate: 0,

  // Never send PII (emails, IPs, etc.) by default
  sendDefaultPii: false,

  // Suppress noisy console output during build/CI
  debug: false,

  beforeSend(event) {
    // Scrub sensitive data — cookies and auth headers
    if (event.request) {
      delete event.request.cookies;
      if (event.request.headers) {
        delete event.request.headers.authorization;
        delete event.request.headers.cookie;
        delete event.request.headers['x-supabase-auth'];
      }
    }

    return event;
  },
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
